import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { em } from '@mantine/core';
import { BaseSyntheticEvent, memo } from 'react';
import styled, { css } from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

interface Props {
	onClick?: (event: BaseSyntheticEvent) => unknown;
	variant: 'off' | 'on' | 'multi';
	className?: string;
	size: 's' | 'm' | 'l';
	disabled?: boolean;
	rhfName?: string;
	/** Index for E2E testing - used as data-testindex attribute */
	index?: number;
}
export enum CheckboxV2Variant {
	off = 'off',
	on = 'on',
	multi = 'multi',
}

export enum CheckboxV2Size {
	s = 's',
	m = 'm',
	l = 'l',
}

const checkBoxSizeMapping: Record<CheckboxV2Size, string> = {
	[CheckboxV2Size.s]: `
        width: ${em(18)};
        min-width: ${em(18)};
        height: ${em(18)};
        border-radius:${em(2)};
        --border-width: ${em(1)};
        ${Icon}.svg {
            width: ${em(10)};
            height: ${em(8)};
        }`,
	[CheckboxV2Size.m]: `
        width: ${em(24)};
        min-width: ${em(24)};
        height: ${em(24)};
        border-radius:${em(4)};
        --border-width: ${em(1.5)};
        ${Icon}.svg {
            width: ${em(14)};
            height: ${em(10)};
        }`,
	[CheckboxV2Size.l]: `
        width: ${em(32)};
        min-width: ${em(32)};
        height: ${em(32)};
        border-radius:${em(4)};
        --border-width: ${em(2)};
        ${Icon}.svg {
            width: ${em(20)};
            height: ${em(14)};
        }`,
};
type ICheckboxV2VariantMapping = {
	[key in CheckboxV2Size]: string;
};
const checkBoxVariantIconMapping: Record<CheckboxV2Variant, ICheckboxV2VariantMapping> = {
	[CheckboxV2Variant.off]: {
		[CheckboxV2Size.s]: 'checkbox-tick-s',
		[CheckboxV2Size.m]: 'checkbox-tick-m',
		[CheckboxV2Size.l]: 'checkbox-tick-l',
	},
	[CheckboxV2Variant.on]: {
		[CheckboxV2Size.s]: 'checkbox-tick-s',
		[CheckboxV2Size.m]: 'checkbox-tick-m',
		[CheckboxV2Size.l]: 'checkbox-tick-l',
	},
	[CheckboxV2Variant.multi]: {
		[CheckboxV2Size.s]: 'checkbox-multi-s',
		[CheckboxV2Size.m]: 'checkbox-multi-m',
		[CheckboxV2Size.l]: 'checkbox-multi-l',
	},
};

type ICheckboxVariantColorMapping = {
	[key in CheckboxV2Variant]: {
		defaultBorderColor?: string;
		defaultBgColor: string;
		focusBorderColor: string;
		focusBgColor: string;
		hoverBgColor: string;
	};
};
const checkBoxVariantStyleMapping: ICheckboxVariantColorMapping = {
	[CheckboxV2Variant.off]: {
		defaultBgColor: defaultTheme.v2.color.white[500],
		defaultBorderColor: defaultTheme.v2.color.neutral[1200],
		focusBgColor: defaultTheme.v2.color.white[500],
		focusBorderColor: defaultTheme.v2.color.primary[800],
		hoverBgColor: defaultTheme.v2.color.secondary[200],
	},
	[CheckboxV2Variant.on]: {
		defaultBgColor: defaultTheme.v2.color.secondary[800],
		focusBgColor: defaultTheme.v2.color.secondary[800],
		focusBorderColor: defaultTheme.v2.color.primary[800],
		hoverBgColor: defaultTheme.v2.color.secondary[1000],
	},
	[CheckboxV2Variant.multi]: {
		defaultBgColor: defaultTheme.v2.color.secondary[800],
		focusBgColor: defaultTheme.v2.color.secondary[800],
		focusBorderColor: defaultTheme.v2.color.primary[800],
		hoverBgColor: defaultTheme.v2.color.secondary[1000],
	},
};
const CheckboxV2 = ({ className, size, variant, onClick, index }: Props) => {
	return (
		<UIElementIdentifier identifier={TEST_ELEMENT_IDS.TABLE_CHECKBOX} index={index}>
			<CheckboxWrapper className={className} tabIndex={0} onClick={onClick}>
				{variant !== CheckboxV2Variant.off && (
					<Icon variant={checkBoxVariantIconMapping[variant][size] as PredefinedIcons} />
				)}
			</CheckboxWrapper>
		</UIElementIdentifier>
	);
};
const CheckboxWrapper = styled.div``;
const Memoized = memo(CheckboxV2);
const Styled = styled(Memoized)`
	display: flex;
	cursor: pointer;
	justify-content: flex-start;
	align-items: center;
	box-sizing: border-box;
	${({ size }) => checkBoxSizeMapping[size]};

	${({ variant }) => `
        --opacity: 0.32;
        --border-style: solid var(--border-width) ${checkBoxVariantStyleMapping[variant].defaultBorderColor};
        color: ${defaultTheme.v2.color.white[500]};
        background-color: ${checkBoxVariantStyleMapping[variant].defaultBgColor};
        border: var(--border-style);

        &:hover {
            background-color: ${checkBoxVariantStyleMapping[variant].hoverBgColor};
        }

        &:focus {
            background-color: ${checkBoxVariantStyleMapping[variant].focusBgColor};
            outline: solid var(--border-width) ${checkBoxVariantStyleMapping[variant].focusBorderColor};
        }

        ${Icon}.svg {
            color: var(--font-color);
            margin: auto;
        }
    `};

	${({ variant, disabled }) =>
		disabled
			? css`
					background-color: ${checkBoxVariantStyleMapping[variant].defaultBgColor};
					opacity: var(--opacity);
					border: var(--border-style);
					pointer-events: none;
				`
			: ``};
`;

export { Styled as Checkbox };
